import {
  Input,
  Search,
  User,
  useDebounce,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../components/molecules/Pagination";
import { useSpecialtyLabels } from "../../hooks/useSpecialtyLabels";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  fetchDoctorsRequest,
  selectDoctorPagination,
  selectDoctorResults,
  selectDoctorsResultsError,
  selectDoctorsResultsLoading,
} from "../../store/reducers/doctorAdminSlice";
import { DoctorPublicResponse } from "../../types/doctorAdmin.definition";
import { notificationMethodLabel } from "../../utils/enum-label-utils";

const ITEMS_PER_PAGE = 10;

export const DoctorsManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const doctors = useAppSelector(selectDoctorResults);
  const pagination = useAppSelector(selectDoctorPagination);
  const isLoading = useAppSelector(selectDoctorsResultsLoading);
  const error = useAppSelector(selectDoctorsResultsError);
  // MED-1523: the doctor payload embeds untranslated specialty labels.
  const specialtyLabel = useSpecialtyLabels();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to page 1 when the search term changes — done during render (not in
  // an effect) so React discards this render and re-runs with currentPage
  // already at 1, instead of the fetch effect below firing once with the
  // stale page and again after the reset commits.
  const previousSearchTermRef = useRef(debouncedSearchTerm);
  if (previousSearchTermRef.current !== debouncedSearchTerm) {
    previousSearchTermRef.current = debouncedSearchTerm;
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }

  useEffect(() => {
    dispatch(
      fetchDoctorsRequest({
        keyword: debouncedSearchTerm,
        page: currentPage - 1,
        size: ITEMS_PER_PAGE,
      })
    );
  }, [dispatch, debouncedSearchTerm, currentPage]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleDoctorClick = (doctor: DoctorPublicResponse) => {
    navigate(`/admin/doctors/${doctor.id}`);
  };

  const totalItems = pagination?.totalCount ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.doctors.title")}
          </h2>
          <p className="text-gray-600">{t("admin.doctors.subtitle")}</p>
        </div>
      </div>

      {/* Search */}
      <div className="page-section">
        <Input
          placeholder={t("admin.doctors.searchPlaceholder")}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg border bg-danger-50 border-danger-200">
          <p className="text-sm text-danger-700">
            {t("admin.doctors.errorLoading")}
          </p>
        </div>
      )}

      {/* Results */}
      <div className="page-section">
        {isLoading ? (
          <div className="py-12 text-center">
            <p className="text-sm text-neutral-500">{t("common.loading")}</p>
          </div>
        ) : doctors.length === 0 ? (
          <div className="py-12 text-center">
            <User className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.doctors.noDoctors")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.doctors.noDoctorsDescription")}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      {t("admin.doctors.table.name")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("admin.doctors.table.phone")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("admin.doctors.table.email")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("admin.doctors.table.specialty")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("admin.doctors.table.notificationMethod")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {doctors.map(doctor => (
                    <tr
                      key={doctor.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleDoctorClick(doctor)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {[doctor.firstName, doctor.lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {doctor.phoneNumber
                          ? formatPhoneNumberForDisplay(doctor.phoneNumber)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {doctor.email ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {doctor.specialties && doctor.specialties.length > 0
                          ? doctor.specialties
                              .map(s => specialtyLabel(s))
                              .filter(Boolean)
                              .join(", ") || "—"
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {doctor.preferredNotificationMethods &&
                        doctor.preferredNotificationMethods.length > 0
                          ? doctor.preferredNotificationMethods
                              .map(method => notificationMethodLabel(t, method))
                              .join(", ")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="page-section">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  showPageNumbers={true}
                  maxVisiblePages={5}
                  showItemsPerPageSelector={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
